import requests from "./httpServices";

/** The filename the server chose, or a sensible default. */
const filenameFromResponse = (response, fallback) => {
  const disposition =
    response.headers?.["content-disposition"] ||
    response.headers?.["Content-Disposition"] ||
    "";
  const match =
    disposition.match(/filename\*=(?:UTF-8'')?([^;]+)/i) ||
    disposition.match(/filename="?([^";]+)"?/i);
  if (!match) return fallback;
  const raw = match[1].replace(/["']/g, "").trim();
  try {
    return decodeURIComponent(raw) || fallback;
  } catch (error) {
    return raw || fallback;
  }
};

/**
 * The message inside a failed blob request.
 *
 * axios hands back a Blob even for the error response, because responseType
 * was set before the status was known. Without this the server's explanation
 * is lost and the user sees a generic failure.
 */
export const readBlobError = async (error) => {
  const data = error?.response?.data;
  if (!data) return null;
  try {
    const text = typeof data.text === "function" ? await data.text() : String(data);
    const parsed = JSON.parse(text);
    return parsed?.error || parsed?.message || null;
  } catch (parseError) {
    return null;
  }
};

const downloadSchoolExport = async (path, teacherIds, fallbackName) => {
  const ids = Array.isArray(teacherIds) ? teacherIds.filter(Boolean) : [];
  const query = ids.length ? `?teacherIds=${encodeURIComponent(ids.join(","))}` : "";
  const response = await requests.getBlob(`/certifications/school/${path}${query}`);
  return {
    blob: response.data,
    filename: filenameFromResponse(response, fallbackName),
  };
};

/**
 * Hand a generated file to the browser.
 *
 * The object URL is revoked on a later tick rather than immediately: Safari
 * cancels the download if the URL is released in the same frame as the click.
 */
export const saveBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
};

const CertificationServices = {
  getCourses: () => requests.get(`/certifications/courses?nocache=${Date.now()}`),
  getMyCertifications: () => requests.get("/certifications/my"),
  upsertCertification: (payload) => requests.post("/certifications/my", payload),
  assignCertificationToTeacher: (teacherId, payload) =>
    requests.post(
      `/certifications/school/${encodeURIComponent(teacherId)}`,
      payload
    ),
  downloadCertificate: async (input, fallbackName = "certificate") => {
    let response;
    let resolvedFallback = fallbackName;

    if (typeof input === "string") {
      response = await requests.getBlob(input);
    } else if (input && typeof input === "object") {
      const {
        certId,
        teacherId,
        fallbackName: customFallback,
        url,
      } = input;
      if (customFallback) {
        resolvedFallback = customFallback;
      }
      if (certId && teacherId) {
        response = await requests.getBlob(
          `/certifications/school/${encodeURIComponent(
            teacherId
          )}/${encodeURIComponent(certId)}/certificate/download`
        );
      } else if (certId) {
        response = await requests.getBlob(
          `/certifications/my/${encodeURIComponent(
            certId
          )}/certificate/download`
        );
      } else if (url) {
        response = await requests.getBlob(url);
      } else {
        throw new Error("No certificate source provided.");
      }
    } else {
      throw new Error("Invalid download input.");
    }

    const blob = response.data;
    const disposition =
      response.headers?.["content-disposition"] ||
      response.headers?.["Content-Disposition"] ||
      "";
    let filename = resolvedFallback;

    if (disposition) {
      const utfMatch = disposition.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
      const asciiMatch = disposition.match(/filename="?([^";]+)"?/i);
      const raw =
        (utfMatch && utfMatch[1]) ||
        (asciiMatch && asciiMatch[1]) ||
        resolvedFallback;
      try {
        filename = decodeURIComponent(raw.replace(/["']/g, "")).trim();
      } catch (error) {
        filename = raw.replace(/["']/g, "").trim();
      }
    }

    if (!filename || filename === "") {
      const source =
        (typeof input === "string" && input) || (input && input.url) || "";
      const parts = source.split("/");
      const lastPart = parts[parts.length - 1] || resolvedFallback;
      filename = lastPart.split("?")[0] || resolvedFallback;
    }

    const contentType =
      response.headers?.["content-type"] ||
      response.headers?.["Content-Type"] ||
      "application/octet-stream";

    return { blob, filename, contentType };
  },
  /**
   * The school's certification exports: a roster report, or printable
   * certificates for staff who have completed training.
   *
   * `teacherIds` is an optional filter on the caller's OWN school roster -
   * the server resolves scope first and applies this afterwards, so it can
   * narrow the result but never reach another school's staff. Omit it to
   * export everyone.
   *
   * Returns { blob, filename } ready for saveBlob. Errors are JSON even
   * though the request asked for a blob, so readBlobError unwraps them -
   * otherwise a "no completed certifications" explanation reaches the
   * administrator as the string "[object Blob]".
   */
  exportSchoolReport: async (format, teacherIds) => {
    const path = format === "pdf" ? "report.pdf" : "report.csv";
    return downloadSchoolExport(path, teacherIds, `training-report.${format}`);
  },

  exportSchoolCertificates: async (teacherIds) =>
    downloadSchoolExport("certificates.pdf", teacherIds, "certificates.pdf"),

  uploadCertificate: (certificationId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    return requests.postWithoutIP(
      `/certifications/my/${encodeURIComponent(certificationId)}/certificate`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    );
  },
  deleteCertificate: (certificationId) =>
    requests.delete(
      `/certifications/my/${encodeURIComponent(certificationId)}/certificate`
    ),
  deleteCertification: (courseKey) =>
    requests.delete(
      `/certifications/my?courseKey=${encodeURIComponent(courseKey)}`
    ),

  // Helper constants and functions for default dates and checks
  ANGELWATCH_CORE_COURSES: [
    "daily_protocols_exterior_threat",
    "interior_active_shooter_procedures",
    "counter_measures_co2_introduction",
    "co2_procedures_protocols_for_room_with_secondary_egress",
    "active_shooter_without_2nd_egress_shelter_in_place",
    "access_control_procedures_radio_communication_shelter_in_place_communication",
    "combative_parent_access_control",
    "congratulations_on_completing_your_course",
    "cpr_2",
    "cpr_1"
  ],

  // DEPRECATED (2026-09-05) - do not call. Returns a hardcoded calendar-2026
  // schedule (core course N due the last day of month N of 2026). Every caller
  // used it as a fallback when no date was stored, so unstarted courses showed
  // invented deadlines that rendered red and overdue, and clearing a due date
  // re-populated it. Kept only as reference for whatever real default policy
  // replaces it; wire nothing to it until that policy is agreed.
  calculateDefaultDates: (courseKey) => {
    if (!courseKey) return { dueByDate: null, renewByDate: null };

    // Normalized list of core course keys (ensure these match DB keys)
    const CORE_KEYS = [
      "daily_protocols_exterior_threat",
      "interior_active_shooter_procedures",
      "counter_measures_co2_introduction",
      "co2_procedures_protocols_for_room_with_secondary_egress",
      "active_shooter_without_2nd_egress_shelter_in_place",
      "access_control_procedures_radio_communication_shelter_in_place_communication",
      "combative_parent_access_control",
      "congratulations_on_completing_your_course",
      "cpr_2",
      "cpr_1"
    ];

    const coreIndex = CORE_KEYS.indexOf(courseKey);

    if (coreIndex !== -1) {
      // Fixed 2026 Schedule: 1 course per month
      // Index 0 -> Jan 2026, Index 1 -> Feb 2026, ...
      const year = 2026;
      const month = coreIndex; // 0-based month (0 = Jan)

      // Calculate last day of the month
      // new Date(year, month + 1, 0) gives the last day of 'month'
      const dueDate = new Date(year, month + 1, 0);
      // Set to end of day to be generous? Standard is usually 00:00 or 23:59. 
      // isOverdue checks if date < now (00:00). So due date should be respected until the day passes.
      // Let's keep strict dates (YYYY-MM-DDT00:00:00) to match input formats often used.
      // But "Due By Jan 31" usually implies you have until the end of Jan 31.
      // Our isOverdue check is: date < now (where now is 00:00 today).
      // So if due is Jan 31, and today is Feb 1. Feb 1 > Jan 31 -> Overdue.
      // If today is Jan 31. Jan 31 !< Jan 31 -> Not Overdue.
      // So setting time to 00:00:00 is correct for date-only comparison.

      // Renew date = 1 year from due date
      const renewDate = new Date(dueDate);
      renewDate.setFullYear(renewDate.getFullYear() + 1);

      return {
        dueByDate: dueDate.toISOString(),
        renewByDate: renewDate.toISOString()
      };
    }
    return { dueByDate: null, renewByDate: null };
  },

  isOverdue: (dateString, isCompleted = false) => {
    if (!dateString) return false;
    if (isCompleted) return false;
    const date = new Date(dateString);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return date < now;
  },

  isExpired: (dateString) => {
    if (!dateString) return false;
    const date = new Date(dateString);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return date < now;
  },
  getSchoolCertifications: () => requests.get("/certifications/school"),
  getTeacherCertificationsAsAdmin: (teacherId) =>
    requests.get(`/certifications/admin/${teacherId}`),
  createCustomCourse: (courseData) => requests.post("/certifications/courses", courseData),
  updateCourse: (courseKey, courseData) => requests.put(`/certifications/courses/${encodeURIComponent(courseKey)}`, courseData),
  deleteCustomCourse: (courseKey) => requests.delete(`/certifications/courses/${encodeURIComponent(courseKey)}`),
  deleteAssignment: (certId) => requests.delete(`/certifications/assignment/${encodeURIComponent(certId)}`),

  // Check and auto-assign certificates for historical course completions
  checkAndAssignHistoricalCertificates: async (teacherId = null) => {
    try {
      const params = teacherId ? { teacherId } : {};
      const response = await requests.post("/certifications/check-and-assign-historical", params);
      return response;
    } catch (error) {
      // Endpoint may not exist yet, silently handle 404 errors
      if (error?.response?.status === 404) {
        // Endpoint doesn't exist, this is okay
        return null;
      }
      // Only log non-404 errors
      console.error("Error checking and assigning historical certificates:", error);
      return null;
    }
  },
};

export default CertificationServices;

